import React, { useEffect, useState } from "react";
import { useTabs } from "../hooks/useTabs";
import { pubCall } from "../utils/PubSub";
import { showError, showMessage } from "../ui/Message";
import Spin from "../ui/Spin";
import Space from "../ui/Space";
import Label from "../ui/Label";
import Input from "../ui/Input";
import Status from "../ui/Status";
import { InfoIcon } from "../utils/Icons";
import { KBD } from "../utils/Keybindings";
import { listen } from "../hooks/Listener";
import { ROUTES } from "../utils/Routes";
import EnumField from "../ui/EnumField";
import { arrayToObject, bm_date, drawPrice } from "../utils/Utils";
import Divider from "../ui/Divider";
import PriceField from "../ui/PriceField";
import POSGiftCardAudit from "./POSGiftCardActivity";
import { createModal } from "../hooks/createModal";

export default function POSGiftCard(props) {

    const { controller = "", route = "", callback = () => { }, giftcard = "", header = {} } = props;

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [max, setMax] = useState(0);


    const {
        balance = 0,
        last_issued_date = 0,
        last_redeemed_date = 0,
        number = "",
        pin = "",
        total_issued = 0,
        total_redeemed = 0,
        activity = []
    } = data;

    const {
        calculated_totals = {}
    } = header;
    const {
        total = 0
    } = calculated_totals;



    const defaults = [
        { key: "redeem", value: 0 }, // from_discount is the field's value 
        { key: "refill", value: 0 }, // to_discount is the customer's discount
        { key: "note", value: "" },
        { key: "history", value: "N" }
    ];
    const { tabs, setTabs, index, setIndex, update, getVal, editProps, next, prev, refs, updateSkip } = useTabs(defaults, "");


    const tabsRef = React.useRef(tabs);

    useEffect(() => {
        tabsRef.current = tabs;
    }, [tabs]);


    const blurField = (_field = index) => {
        const el = refs.current[_field];
        if (el && typeof el.blur === 'function') {
            el.blur();
        }
    }

    const focusField = (_field = index) => {
        const el = refs.current[_field];
        if (el && typeof el.focus === 'function') {
            el.focus();
        }
    }

    const redeemAmount = Number(
        tabs.find((tab) => tab.key === "redeem")?.value
    ) || 0;

    const refillAmount = Number(
        tabs.find((tab) => tab.key === "refill")?.value
    ) || 0;

    const redeemDisabled = refillAmount > 0;
    const refillDisabled = redeemAmount > 0;
    const noteDisabled = redeemAmount > 0;

    useEffect(() => {
        setTabs((currentTabs) =>
            currentTabs.map((tab) => {
                let skip = tab.skip;

                if (tab.key === "redeem") {
                    skip = redeemDisabled;
                }

                if (tab.key === "refill") {
                    skip = refillDisabled;
                }

                if (tab.key === "note") {
                    skip = noteDisabled;
                }

                return tab.skip === skip
                    ? tab
                    : { ...tab, skip };
            })
        );

        // Immediately leave a field if it has just become disabled.
        if (
            (index === "redeem" && redeemDisabled) ||
            (index === "refill" && refillDisabled) ||
            (index === "note" && noteDisabled)
        ) {
            setIndex("history");
        }
    }, [
        redeemDisabled,
        refillDisabled,
        noteDisabled,
        index,
        setTabs,
        setIndex
    ]);



    const fetchGiftCard = () => {
        pubCall("gift_card.getBalance", { gift_card_number: giftcard }, (_s, _r) => {
            if (_s) {

                const cardBalance = Number(_r?.balance) || 0;
                const amountDue = Math.abs(Number(total) || 0);


                const redeemAmount = Math.min(
                    cardBalance,
                    amountDue
                );
                setMax(redeemAmount);

                update("redeem", redeemAmount);

                requestAnimationFrame(() => {
                    setIndex("redeem");
                })


                setData(_r);
            } else {
                showError(_r);
            }
        })
    }

    useEffect(fetchGiftCard, []);

    const formatCode = (_code = "") => {
        const gc = _code.replaceAll(" ", "");
        return [
            gc.slice(0, 2),
            gc.slice(2, 6),
            gc.slice(6, 10),
            gc.slice(10),
        ].filter(Boolean).join(" ");
    };


    const statusus = {
        note: "Use this to help identify the card (stored in the Customer PO field)",
        history: "Audit/Local/No",
    };


    const moveNext = () => {
        requestAnimationFrame(() => {
            next();
        });
    };

    const movePrev = () => {
        requestAnimationFrame(() => {
            prev();
        });
    };

    useEffect(() => {

        refs?.current?.[index]?.focus?.();
        refs?.current?.[index]?.select?.();

        const actions = [
            { key: KBD.ESCAPE, action: () => {
                blurField(); 
                callback();} },
            { key: KBD.ARROW_DOWN, action: () => moveNext() },
            { key: KBD.ARROW_UP, action: () => movePrev() },
            { key: KBD.TAB, action: () => moveNext() },
            { key: KBD.SHIFT_TAB, action: () => movePrev() },
            { key: KBD.PAGE_DOWN, action: (e) => save() }
        ]

        const cleanup = listen(
            controller,
            route,
            actions,
            false,
            [KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.ESCAPE, KBD.PAGE_DOWN]
        );

        return cleanup;
    }, [index]);



    const card_history = [
        { label: "Audit", value: "A", key: KBD.A },
        { label: "Local", value: "L", key: KBD.L },
        { label: "No", value: "N", key: KBD.N }
    ];



    const save = () => {
        blurField();
        const ret = arrayToObject(tabsRef.current);
        callback(ret);
    };


    const drawAudit = (_gift_card) => {
        blurField();
        const return_route = window.bm_state.value;
        window.bm_state.value = ROUTES.POS.GIFT_CARD_ACTIVITY;

        createModal({
            component: <POSGiftCardAudit
                callback_on_single_result
                controller={controller}
                data={data}
                gift_card={_gift_card}
                header={header}
                route={ROUTES.POS.GIFT_CARD_ACTIVITY}
            />,
            closable: true,
            title: "Gift Card Activity",
            callback: (e) => {
                window.bm_state.value = return_route;
                focusField();
            }
        });
    }

    return (
        <div style={{ "width": "510px" }}>
            <Spin loading={loading}>
                <div className="shim" /><div className="shim" />
                <Space>
                    <Label>Gift card#</Label>
                    <Label style={{ "color": "var(--text)" }}>{formatCode(number)} {pin}</Label>
                </Space>
                <div className="shim" /><div className="shim" />
                <Space>
                    <Label width={100} style={{ "textAlign": "left" }}>Last issued</Label>
                    <Label width={100} style={{ "color": "var(--text)", "textAlign": "left" }}>{bm_date(last_issued_date)}</Label>
                    <Label width={100} style={{ "textAlign": "left" }}>Total issued</Label>
                    <Label style={{ "color": "var(--text)" }}>{drawPrice(total_issued, true)}</Label>
                </Space>
                <div className="shim" />
                <Space>
                    <Label width={100} style={{ "textAlign": "left" }}>Last redeemed</Label>
                    <Label width={100} style={{ "color": "var(--text)", "textAlign": "left" }}>{bm_date(last_redeemed_date)}</Label>
                    <Label width={100} style={{ "textAlign": "left" }}>Total redeemed</Label>
                    <Label style={{ "color": "var(--text)" }}>{drawPrice(total_redeemed, true)}</Label>
                </Space>
                <div className="shim" />
                <Divider />
                <div className="shim" />
                <Space>
                    <Label width={300}>Present card balance</Label>
                    <Label style={{ "color": "var(--text-red)" }}>{drawPrice(balance, true)}</Label>
                </Space>
                <div className="shim" />
                <Space>
                    <Label width={300}>Amount to redeem (max 30.00)</Label>
                    <PriceField max={max} width={70} {...editProps("redeem")} style={{ "textAlign": "right" }} onChange={(_v) => {
                        
                        if(_v > balance){
                            update("redeem", balance)
                        } else {
                            update("redeem", _v)
                        }
                        
                    }} className={(redeemDisabled) ? "dim" : ""} edit={!redeemDisabled} onEnter={() => next()} />
                </Space>
                <div className="shim" />
                <Space>
                    <Label width={300}>Card refull amount (if not redeeming)</Label>
                    <PriceField width={70} {...editProps("refill")} style={{ "textAlign": "right" }} className={(refillDisabled) ? "dim" : ""} edit={!refillDisabled} onEnter={() => next()} />
                </Space>
                <div className="shim" />
                <Space>
                    <Label width={300}>Note or recipient name</Label>
                    <Input width={150} {...editProps("note")} className={(noteDisabled) ? "dim" : ""} edit={!noteDisabled} />
                </Space>
                <div className="shim" />
                <Space>
                    <Label width={300}>Show card history?</Label>
                    <EnumField width={70} {...editProps("history")} onChange={() => {
                        drawAudit();
                    }} options={card_history} />
                </Space>
                <div className="shim" /><div className="shim" />
            </Spin>
            <div className="shim" />
            <Status type="active"><InfoIcon />{statusus[index]}</Status>
        </div>
    )
}